import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import ProductSearchForm from '../components/ProductSearchForm';
import AdminNav from '../components/admin/AdminNav';
import MultiSelect from '../components/admin/MultiSelect';
import fixEncoding from '../utils/fixEncoding';
import '../styles/pages/AdminProductsPage.scss';

const API_URL = process.env.REACT_APP_API_URL;
const S3_URL  = process.env.REACT_APP_S3_BASE_URL;

const AdminProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial values from URL so state is restored after navigation
  const initialFilters = useMemo(() => ({
    search:    searchParams.get('search')    || '',
    warehouse: searchParams.get('warehouse') || '',
    category:  searchParams.get('category') || '',
  }), []); // eslint-disable-line react-hooks/exhaustive-deps
  const initialPage = parseInt(searchParams.get('page') || '1', 10);

  const [products,            setProducts]           = useState([]);
  const [loading,             setLoading]            = useState(true);
  const [searching,           setSearching]          = useState(false);
  const [error,               setError]              = useState('');
  const [searchFilters,       setSearchFilters]      = useState(initialFilters);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [pagination,          setPagination]         = useState({ currentPage: initialPage, totalPages: 1, totalCount: 0 });
  const [isSearching,         setIsSearching]        = useState(
    !!(initialFilters.search || initialFilters.warehouse || initialFilters.category)
  );

  // --- Selección masiva ---
  const [allTaxonomies, setAllTaxonomies] = useState([]); // [{id, name, subcategories:[{id,name}]}]
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCats,    setBulkCats]    = useState([]);
  const [bulkSubs,    setBulkSubs]    = useState([]);
  const [bulkMode,    setBulkMode]    = useState('add'); // 'add' | 'replace'
  const [bulkLock,    setBulkLock]    = useState(true);
  const [bulkBusy,    setBulkBusy]    = useState(false);

  const searchTimeoutRef = useRef(null);

  const clearSelection = useCallback(() => { setSelectedIds([]); setBulkCats([]); setBulkSubs([]); }, []);

  // Subcategorías disponibles según las categorías elegidas en la barra masiva
  const bulkSubOptions = useMemo(() => (
    allTaxonomies
      .filter(cat => bulkCats.includes(cat.id))
      .flatMap(cat => [
        { value: `group-${cat.id}`, label: fixEncoding(cat.name), isGroup: true },
        ...(cat.subcategories || []).map(s => ({ value: s.id, label: fixEncoding(s.name) })),
      ])
  ), [allTaxonomies, bulkCats]);

  // Al cambiar las categorías masivas, se descartan las subcategorías que ya no correspondan
  const onBulkCatsChange = useCallback((ids) => {
    const allowed = new Set(
      allTaxonomies.filter(c => ids.includes(c.id)).flatMap(c => (c.subcategories || []).map(s => s.id))
    );
    setBulkCats(ids);
    setBulkSubs(prev => prev.filter(sid => allowed.has(sid)));
  }, [allTaxonomies]);

  const toggleRow = useCallback((id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const syncURL = useCallback((filters, page) => {
    const params = {};
    if (filters.search)    params.search    = filters.search;
    if (filters.warehouse) params.warehouse = filters.warehouse;
    if (filters.category)  params.category  = filters.category;
    if (page > 1)          params.page      = String(page);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const fetchProducts = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admToken');
      const response = await axios.get(`${API_URL}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 40 },
      });
      const { products, totalPages, currentPage, totalCount } = response.data;
      setProducts(products);
      setPagination({ totalPages, currentPage, totalCount });
    } catch (err) {
      setError('Error al cargar los productos. Por favor, intente de nuevo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchProducts = useCallback(async (page = 1, filters = {}) => {
    setSearching(true);
    setIsSearching(true);
    try {
      const token = localStorage.getItem('admToken');
      const response = await axios.get(`${API_URL}/admin/products/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 40, ...filters },
      });
      const { products, totalPages, currentPage, totalCount } = response.data;
      setProducts(products);
      setPagination({ totalPages, currentPage, totalCount });
    } catch (err) {
      setError('Error al buscar productos. Por favor, intente de nuevo.');
      console.error(err);
    } finally {
      setSearching(false);
      setLoading(false);
    }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('admToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [opts, tax] = await Promise.all([
        axios.get(`${API_URL}/admin/products/filter-options`, { headers }),
        axios.get(`${API_URL}/admin/products/taxonomies`, { headers }),
      ]);
      setAvailableWarehouses(opts.data.warehouses);
      setAvailableCategories(opts.data.categories);
      setAllTaxonomies(tax.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, []);

  const handleSearch = useCallback((filters) => {
    setSearchFilters(filters);
    syncURL(filters, 1);
    setSelectedIds([]);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const hasFilters = filters.search || filters.warehouse || filters.category;

    searchTimeoutRef.current = setTimeout(() => {
      if (!hasFilters) {
        setIsSearching(false);
        fetchProducts(1);
      } else {
        searchProducts(1, filters);
      }
    }, 600);
  }, [syncURL, fetchProducts, searchProducts]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      syncURL(searchFilters, newPage);
      setSelectedIds([]);
      if (isSearching) {
        searchProducts(newPage, searchFilters);
      } else {
        fetchProducts(newPage);
      }
    }
  }, [pagination.totalPages, syncURL, searchFilters, isSearching, searchProducts, fetchProducts]);

  const handleDelete = async (productId, productTitle) => {
    const isConfirmed = window.confirm(`¿Estás seguro de que quieres eliminar el producto "${productTitle}"? Esta acción no se puede deshacer.`);
    if (isConfirmed) {
      try {
        const token = localStorage.getItem('admToken');
        await axios.delete(`${API_URL}/admin/products/${productId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert(`Producto "${productTitle}" eliminado exitosamente.`);
        if (isSearching) {
          searchProducts(pagination.currentPage, searchFilters);
        } else {
          fetchProducts(pagination.currentPage);
        }
      } catch (err) {
        alert('Error al eliminar el producto. Por favor, revisa la consola.');
        console.error('Error deleting product:', err);
      }
    }
  };

  const refreshCurrentView = useCallback(() => {
    if (isSearching) searchProducts(pagination.currentPage, searchFilters);
    else fetchProducts(pagination.currentPage);
  }, [isSearching, searchProducts, fetchProducts, pagination.currentPage, searchFilters]);

  const allPageSelected = products.length > 0 && products.every(p => selectedIds.includes(p.id));

  const toggleSelectAllPage = useCallback(() => {
    setSelectedIds(prev => {
      const pageIds = products.map(p => p.id);
      const allIn = pageIds.every(id => prev.includes(id));
      return allIn ? prev.filter(id => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])];
    });
  }, [products]);

  const handleBulkApply = useCallback(async () => {
    if (selectedIds.length === 0 || bulkCats.length === 0) return;
    const verb = bulkMode === 'replace' ? 'REEMPLAZAR las categorías de' : 'agregar la(s) categoría(s) a';
    if (!window.confirm(`¿${verb} ${selectedIds.length} producto(s)?`)) return;
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('admToken');
      const { data } = await axios.put(
        `${API_URL}/admin/products/bulk-categories`,
        {
          productIds: selectedIds,
          categoryIds: bulkCats,
          subcategoryIds: bulkSubs,
          mode: bulkMode,
          lockCategories: bulkLock,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      alert(`${data.updated} producto(s) actualizados.`);
      clearSelection();
      refreshCurrentView();
    } catch (err) {
      alert(err.response?.data?.message || 'Error en la asignación masiva.');
      console.error(err);
    } finally {
      setBulkBusy(false);
    }
  }, [selectedIds, bulkCats, bulkSubs, bulkMode, bulkLock, clearSelection, refreshCurrentView]);

  const searchFormProps = useMemo(() => ({
    onSearch:   handleSearch,
    filters:    searchFilters,
    warehouses: availableWarehouses,
    categories: availableCategories,
  }), [handleSearch, searchFilters, availableWarehouses, availableCategories]);

  useEffect(() => {
    fetchFilterOptions();
    const hasFilters = initialFilters.search || initialFilters.warehouse || initialFilters.category;
    if (hasFilters) {
      searchProducts(initialPage, initialFilters);
    } else {
      fetchProducts(initialPage);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="container">Cargando productos...</div>;
  if (error)   return <div className="container alert alert-danger">{error}</div>;

  return (
    <div className="admin-products-page container">
      <header className="admin-products-page__header">
        <h1>Gestión de productos ({pagination.totalCount})</h1>
      </header>

      <AdminNav
        primaryAction={
          <Link to="/admin/products/new" className="admin-nav__btn-primary">
            Crear nuevo producto
          </Link>
        }
      />

      <ProductSearchForm {...searchFormProps} />

      {selectedIds.length > 0 && (
        <div className="admin-bulk-bar">
          <span className="admin-bulk-bar__count">{selectedIds.length} seleccionado(s)</span>

          <div className="admin-bulk-bar__field">
            <MultiSelect
              placeholder="Categoría(s) a asignar..."
              options={allTaxonomies.map(c => ({ value: c.id, label: fixEncoding(c.name) }))}
              value={bulkCats}
              onChange={onBulkCatsChange}
            />
          </div>

          <div className="admin-bulk-bar__field">
            <MultiSelect
              placeholder={bulkCats.length === 0 ? 'Elige categoría primero...' : 'Subcategoría(s)...'}
              options={bulkSubOptions}
              value={bulkSubs}
              onChange={setBulkSubs}
            />
          </div>

          <label className="admin-bulk-bar__opt">
            <input type="radio" name="bulkMode" checked={bulkMode === 'add'}
              onChange={() => setBulkMode('add')} /> Agregar
          </label>
          <label className="admin-bulk-bar__opt">
            <input type="radio" name="bulkMode" checked={bulkMode === 'replace'}
              onChange={() => setBulkMode('replace')} /> Reemplazar
          </label>
          <label className="admin-bulk-bar__opt">
            <input type="checkbox" checked={bulkLock}
              onChange={(e) => setBulkLock(e.target.checked)} /> 🔒 Bloquear para sync
          </label>

          <button className="btn btn-sm btn-warning" disabled={bulkBusy || bulkCats.length === 0}
            onClick={handleBulkApply}>
            {bulkBusy ? 'Aplicando…' : 'Aplicar'}
          </button>
          <button className="btn btn-sm" onClick={clearSelection} disabled={bulkBusy}>
            Cancelar
          </button>
        </div>
      )}

      <div className={`table-responsive${searching ? ' table-responsive--searching' : ''}`}>
        {searching && <div className="admin-products-page__search-indicator">Buscando...</div>}
        <table className="admin-products-page__table">
          <thead>
            <tr>
              <th className="admin-products-page__check">
                <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAllPage}
                  title="Seleccionar todos los de esta página" />
              </th>
              <th>Imagen</th>
              <th>Título</th>
              <th>Referencia</th>
              <th>Proveedor</th>
              <th>Cat. bodega</th>
              <th>Sub. bodega</th>
              <th>Categorías en Stock</th>
              <th>Sub. en stock</th>
              <th>Cantidad</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} className={selectedIds.includes(product.id) ? 'is-selected' : ''}>
                <td className="admin-products-page__check">
                  <input type="checkbox" checked={selectedIds.includes(product.id)}
                    onChange={() => toggleRow(product.id)} />
                </td>
                <td className="img-cell">
                  {product.image_files?.length
                    ? <img
                        src={`${S3_URL}/thumbnails/${product.image_files[0]}`}
                        alt={product.title}
                        className="admin-products-page__thumb"
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    : <div className="admin-products-page__no-img">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <path d="M21 15l-5-5L5 21"/>
                        </svg>
                      </div>
                  }
                </td>
                <td>
                  <a href={`/productos/${product.slug}`} target="_blank" rel="noopener noreferrer">
                    {fixEncoding(product.title)}
                  </a>
                  {product.locked_categories && (
                    <span className="admin-products-page__lock" title="Categorías bloqueadas para sync">🔒</span>
                  )}
                  {product.is_deal && (
                    <span className="admin-products-page__lock" title="Aparece en Ofertas">🔥</span>
                  )}
                </td>
                <td>{product.reference}</td>
                <td>{product.warehouse}</td>
                <td className="sub-cell">
                  {product.category_bodega
                    ? fixEncoding(product.category_bodega)
                    : <span className="admin-products-page__empty">—</span>}
                </td>
                <td className="sub-cell">
                  {product.subcategory_bodega
                    ? fixEncoding(product.subcategory_bodega)
                    : <span className="admin-products-page__empty">—</span>}
                </td>
                <td>
                  {product.Categories.map(cat => fixEncoding(cat.name)).join(', ')}
                </td>
                <td className="sub-cell">
                  {product.Subcategories?.length
                    ? product.Subcategories.map(s => fixEncoding(s.name)).join(', ')
                    : <span className="admin-products-page__empty">—</span>
                  }
                </td>
                <td className="stock-cell">
                  {(() => {
                    const v = product.variations;
                    if (!v || typeof v !== 'object') return <span className="admin-products-page__empty">—</span>;
                    const total = Object.values(v).reduce((sum, n) => sum + (Number(n) || 0), 0);
                    return total > 0
                      ? <span className="admin-products-page__stock">{total.toLocaleString('es-CO')}</span>
                      : <span className="admin-products-page__empty">0</span>;
                  })()}
                </td>
                <td className="actions-cell">
                  <Link to={`/admin/products/edit/${product.id}`} className="btn btn-sm btn-warning">
                    Editar
                  </Link>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(product.id, product.title)}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <nav className="admin-products-page__pagination">
        <button
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          disabled={pagination.currentPage === 1}
        >
          &laquo; Anterior
        </button>
        <span>
          Página {pagination.currentPage} de {pagination.totalPages}
        </span>
        <button
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          disabled={pagination.currentPage === pagination.totalPages}
        >
          Siguiente &raquo;
        </button>
      </nav>
    </div>
  );
};

export default AdminProductsPage;
